import React, { useEffect, useState, useMemo } from "react";
import { StyleSheet, TouchableOpacity, View, ScrollView } from "react-native";
import { colors } from "src/assets";
import { useNavigation } from "@react-navigation/native";
import {
  Typography,
  FormControlLabel,
  FormControlGroup,
  Input,
  AmountBanner,
  BottomActions,
  Button,
} from "src/components";
import { useForm, useController } from "react-hook-form";
import * as formValidators from "src/utils/form-validator";
import { EventRegister } from "react-native-event-listeners";
import {
  navigationRef,
  useAccount,
  useBank,
  useBanks,
  useWithdrawalAction,
} from "src/hooks";
import LocalText from "src/local";
import alert from "src/components/alert";
import { RootStackScreenProps } from "src/navigation/types";

type ControlProps = {
  control: any;
  error?: string;
  onChange?: () => void;
};

const AmountControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({ control, defaultValue: 0, name: "amount" });
  return (
    <Input
      label={LocalText.screens.withdrawalRequest.amountControl.text}
      keyboardType={"number-pad"}
      placeholder={"0"}
      value={`${field.value}`}
      onChangeText={(text: string) => {
        field.onChange(parseInt(text) || 0);
      }}
      error={error}
    />
  );
};

const BankControl: React.FC<ControlProps> = ({ control, error, onChange }) => {
  const [banks] = useBanks();
  const [items, setItems] = useState<
    { id: string; label: string; search: string }[]
  >([]);
  useEffect(() => {
    const localItems = banks.map((item) => ({
      id: item.id,
      label: item.name || "Unknown",
      search: `${item.kana} ${item.hira} ${item.roma}`,
    }));
    localItems.sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      }
      if (a.id > b.id) {
        return 1;
      }
      return 0;
    });
    setItems(localItems);
  }, [banks]);
  const { field } = useController({
    control,
    defaultValue: undefined,
    name: "bank",
  });
  const eventName = "WithdrawalBankEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v);
      if (onChange) {
        onChange();
      }
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field, onChange]);
  // TODO: Add custom memo
  return (
    <FormControlLabel
      text={LocalText.screens.withdrawalRequest.bankControl.text}
      detail={items.find((item) => item.id === field.value)?.label || "(必須)"}
      error={error}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName,
          title: LocalText.screens.withdrawalRequest.bankControl.text,
          helper: LocalText.screens.withdrawalRequest.bankControl.helper,
          selectedId: field.value,
          items,
          isSearchable: true,
        });
      }}
    />
  );
};

const BankBranchControl: React.FC<ControlProps & { bankId?: string }> = ({
  bankId,
  control,
  error,
}) => {
  const [bank] = useBank(bankId);
  const items = useMemo(
    () =>
      (bank?.branches || []).map((item) => ({
        id: item.code,
        label: item.name || "Unknown",
        search: `${item.kana} ${item.hira} ${item.roma}`,
      })),
    [bank]
  );
  const { field } = useController({
    control,
    defaultValue: undefined,
    name: "branch",
  });
  const eventName = "WithdrawalBranchEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v);
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);

  return (
    <React.Fragment>
      {bank ? (
        <FormControlLabel
          text={LocalText.screens.withdrawalRequest.bankBranchControl.text}
          detail={
            items.find((item) => item.id === field.value)?.label || "(必須)"
          }
          error={error}
          rightIcon={"chevron"}
          onPress={() => {
            navigationRef.current?.navigate("CustomSelectionList", {
              eventName,
              title: LocalText.screens.withdrawalRequest.bankBranchControl.text,
              helper:
                LocalText.screens.withdrawalRequest.bankBranchControl.helper,
              selectedId: field.value,
              items,
              isSearchable: true,
            });
          }}
        />
      ) : null}
    </React.Fragment>
  );
};

const AccountTypeControl: React.FC<ControlProps> = ({ control, error }) => {
  const items = [
    { key: "1", label: "普通" },
    { key: "2", label: "当座" },
    { key: "4", label: "貯蓄" },
  ];
  const { field } = useController({
    control,
    defaultValue: undefined,
    name: "accountType",
  });
  const eventName = "WithdrawalAccountTypeEvent";
  useEffect(() => {
    const listener = EventRegister.addEventListener(eventName, (v: string) => {
      field.onChange(v);
    });
    return () => {
      EventRegister.removeEventListener(listener as string);
    };
  }, [field]);
  return (
    <FormControlLabel
      text={LocalText.screens.withdrawalRequest.accountTypeControl.text}
      detail={items.find((item) => item.key === field.value)?.label || "(必須)"}
      error={error}
      rightIcon={"chevron"}
      onPress={() => {
        navigationRef.current?.navigate("CustomSelectionList", {
          eventName,
          title: LocalText.screens.withdrawalRequest.accountTypeControl.text,
          selectedId: field.value,
          items: items.map((item) => ({
            id: item.key,
            label: item.label,
          })),
        });
      }}
    />
  );
};

const AccountNumberControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({
    control,
    defaultValue: "",
    name: "accountNumber",
  });
  return (
    <Input
      label={LocalText.screens.withdrawalRequest.accountNumberControl.text}
      placeholder={
        LocalText.screens.withdrawalRequest.accountNumberControl.placeholder
      }
      value={field.value}
      onChangeText={field.onChange}
      error={error}
    />
  );
};

const AccountHolderControl: React.FC<ControlProps> = ({ control, error }) => {
  const { field } = useController({
    control,
    defaultValue: "",
    name: "accountHolder",
  });
  return (
    <Input
      label={LocalText.screens.withdrawalRequest.accountHolderControl.text}
      placeholder={
        LocalText.screens.withdrawalRequest.accountHolderControl.placeholder
      }
      value={field.value}
      onChangeText={field.onChange}
      error={error}
    />
  );
};

export type WithdrawalRequestNavigationParam = undefined;

type FormValues = {
  amount: number;
  bank: string;
  branch: string;
  accountType: string;
  accountNumber: string;
  accountHolder: string;
};

export const WithdrawalRequestScreen: React.FC = () => {
  const navigation =
    useNavigation<RootStackScreenProps<"WithdrawalRequest">["navigation"]>();
  const [account] = useAccount();
  const [withdrawalHandler, withdrawalLoading] = useWithdrawalAction();
  const [, setTimestamp] = useState<number>(Date.now());
  const {
    register,
    control,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      amount: 0,
      bank: undefined,
      branch: undefined,
      accountType: undefined,
      accountNumber: "",
      accountHolder: "",
    },
  });
  const [bank] = useBank(getValues("bank"));
  useEffect(() => {
    register("amount", {
      validate: (value: number) => {
        if (value <= 0) {
          return LocalText.screens.withdrawalRequest.amountValidationRequired;
        }
        if (!account || value > account.sales) {
          return LocalText.screens.withdrawalRequest.amountValidationUnexpeced;
        }
        return true;
      },
    });
    register("bank", {
      validate: (value: string | undefined) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.withdrawalRequest.bankValidationRequired;
        }
        return true;
      },
    });
    register("branch", {
      validate: (value: string | undefined) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.withdrawalRequest.branchValidationRequired;
        }
        return true;
      },
    });
    register("accountType", {
      validate: (value: string | undefined) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.withdrawalRequest
            .accountTypeValidationRequired;
        }
        return true;
      },
    });
    register("accountNumber", {
      validate: (value: string | undefined) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.withdrawalRequest
            .accountNumberValidationRequired;
        }
        return true;
      },
    });
    register("accountHolder", {
      validate: (value: string) => {
        if (!formValidators.RequireValidator(value)) {
          return LocalText.screens.withdrawalRequest
            .accountHolderValidationRequired;
        }
        if (!new RegExp("^[\u30A0-\u30FF]*$", "g").test(value)) {
          return LocalText.screens.withdrawalRequest
            .accountHolderValidationFormat;
        }
        return true;
      },
    });
  }, [register, account]);

  const onDone = async (data: FormValues) => {
    if (
      await withdrawalHandler({
        amount: data.amount,
        bankCode: data.bank,
        bankName: bank?.name || "unknown",
        branchCode: data.branch,
        branchName:
          bank?.branches?.find(
            (item) => item.id === `${data.bank}-${data.branch}`
          )?.name || "unknown",
        accountType: data.accountType,
        accountHolder: data.accountHolder,
        accountNumber: data.accountNumber,
      })
    ) {
      alert(
        LocalText.screens.withdrawalRequest.doneAlertTitle,
        undefined,
        [
          {
            text: LocalText.common.ok,
            onPress: () => navigation.goBack(),
          },
        ],
        { cancelable: false }
      );
    }
  };

  return (
    <View style={styles.root}>
      <ScrollView style={styles.scrollView}>
        {account ? (
          <AmountBanner
            containerStyle={styles.banner}
            text={LocalText.screens.withdrawalRequest.sales}
            value={account?.sales}
          />
        ) : null}
        <FormControlGroup style={styles.formGroup}>
          <AmountControl control={control} error={errors.amount?.message} />
        </FormControlGroup>
        <TouchableOpacity
          onPress={() => navigation.push("WithdrawalDescription")}
        >
          <Typography
            containerStyle={styles.footer}
            text={LocalText.screens.withdrawalRequest.description}
            variant={"section"}
          />
        </TouchableOpacity>

        <Typography
          text={LocalText.screens.withdrawalRequest.transferSection}
          variant={"section"}
        />
        <FormControlGroup style={styles.formGroup}>
          <BankControl
            control={control}
            error={errors.bank?.message}
            onChange={() => setTimestamp(Date.now())}
          />
          <BankBranchControl
            bankId={getValues("bank")}
            control={control}
            error={errors.branch?.message}
          />
          <AccountTypeControl
            control={control}
            error={errors.accountType?.message}
          />
          <AccountNumberControl
            control={control}
            error={errors.accountNumber?.message}
          />
          <AccountHolderControl
            control={control}
            error={errors.accountHolder?.message}
          />
        </FormControlGroup>
      </ScrollView>
      <BottomActions>
        <Button
          variant={"secondary"}
          icon={"chevron"}
          iconColor={colors.chokuRed}
          iconSize={12}
          title={LocalText.common.back}
          onPress={() => navigation.goBack()}
        />

        <Button
          variant={"primary"}
          loading={withdrawalLoading}
          icon={"check"}
          title={LocalText.screens.withdrawalRequest.process}
          onPress={handleSubmit(onDone)}
        />
      </BottomActions>
    </View>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    backgroundColor: colors.white,
  },
  scrollView: {
    flex: 1,
    backgroundColor: colors.gray1,
  },
  banner: {
    marginHorizontal: 16,
    marginVertical: 8,
  },
  footer: {
    paddingTop: 0,
  },
  formGroup: {
    marginVertical: 8,
  },
});
